import { usePostUser } from '@api';
import { AdminModalType, ContactCountryCodeEnum, PathnameEnum, UserRoleEnum } from '@constant';
import { useSelector } from '@stores';
import { IPostUserBody } from '@types';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';

//어드민 - 회원정보생성 페이지 로직에 관여
export const useAdminUserCreate = () => {
  // 입력값 상태 관리
  const [formData, setFormData] = useState<IPostUserBody & { countryCode: ContactCountryCodeEnum }>({
    name: '',
    email: '',
    password: '',
    contact: '',
    countryCode: ContactCountryCodeEnum['대한민국 (+82)'],
    userIps: [{ nickname: '', ip: '' }], // 최초 한 줄의 빈 필드로 시작
    role: UserRoleEnum['일반 회원']
  });
  const [passwordConfirm, setPasswordConfirm] = useState('');
  // 에러 상태 관리
  const [errors, setErrors] = useState({
    name: '',
    email: '',
    password: '',
    passwordConfirm: '',
    contact: '',
    userIps: [{ nickname: '', ip: '' }] as { nickname: string; ip: string }[]
  });
  const [modalType, setModalType] = useState<AdminModalType>(AdminModalType.USER_CREATE_CANCEL);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);

  const loginUserRole = useSelector((state) => state.auth.role);

  const navigate = useNavigate();

  // 필드별 ref 관리
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const passwordConfirmRef = useRef<HTMLInputElement>(null);
  const contactRef = useRef<HTMLDivElement>(null);
  const ipRefs = useRef<(HTMLInputElement | null)[]>([]);

  const { mutateAsync: postUserMutateAsync, isPending: isPostUserPending } = usePostUser();

  const handleInputChange = (field: keyof IPostUserBody | 'countryCode', value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
    // 입력 시 해당 필드의 에러 초기화
    if (field in errors) {
      setErrors((prev) => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handlePasswordConfirmChange = (value: string) => {
    setPasswordConfirm(value);
    // 비밀번호 확인 입력 시 에러 초기화
    setErrors((prev) => ({
      ...prev,
      passwordConfirm: ''
    }));
  };

  // 연락처 필드 값 변경 (숫자만 허용)
  const handleContactChange = (value: string) => {
    // 숫자만 허용 (빈 문자열 또는 숫자만 포함된 문자열)
    if (value === '' || /^\d+$/.test(value)) {
      setFormData((prev) => ({
        ...prev,
        contact: value
      }));
      // 입력 시 에러 초기화
      setErrors((prev) => ({
        ...prev,
        contact: ''
      }));
    }
    // 숫자가 아닌 문자가 포함되어 있으면 상태 업데이트를 하지 않음 (입력 무시)
  };

  // IP 필드 값 변경
  const handleIpChange = (index: number, field: 'nickname' | 'ip', value: string) => {
    setFormData((prev) => ({
      ...prev,
      userIps: prev.userIps.map((ip, i) => (i === index ? { ...ip, [field]: value } : ip))
    }));
    // 해당 IP 필드의 에러 초기화
    setErrors((prev) => ({
      ...prev,
      userIps: prev.userIps.map((ipError, i) => (i === index ? { ...ipError, [field]: '' } : ipError))
    }));
  };

  // IP 추가 (새로운 빈 줄 추가)
  const handleAddIp = () => {
    setFormData((prev) => ({
      ...prev,
      userIps: [...prev.userIps, { nickname: '', ip: '' }]
    }));
    // 에러 배열에도 빈 에러 추가
    setErrors((prev) => ({
      ...prev,
      userIps: [...prev.userIps, { nickname: '', ip: '' }]
    }));
  };

  // IP 삭제
  const handleRemoveIp = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      userIps: prev.userIps.filter((_, i) => i !== index)
    }));
    // 에러 배열에서도 해당 인덱스 제거
    setErrors((prev) => ({
      ...prev,
      userIps: prev.userIps.filter((_, i) => i !== index)
    }));
  };

  // IP 추가 버튼 활성화 조건: 모든 기존 IP가 채워져 있을 때
  const isAddIpButtonEnabled = formData.userIps.every((ip) => ip.nickname.trim() && ip.ip.trim());

  // 로그인한 유저의 role에 따라 roleOptions 분기
  const getRoleOptions = (fetchedRole?: UserRoleEnum) => {
    // 루트 관리자 유저의 정보를 변경하는 경우, 권한 Select에 '루트 관리자'를 보여주어야 함
    if (fetchedRole && fetchedRole === UserRoleEnum['루트 관리자']) {
      return [{ label: '루트 관리자', value: UserRoleEnum['루트 관리자'] }];
    }

    if (loginUserRole === UserRoleEnum['루트 관리자']) {
      // 루트 관리자인 경우: 최고 관리자, 데이터 관리자, 일반 회원
      return [
        { label: '최고 관리자', value: UserRoleEnum['최고 관리자'] },
        { label: '데이터 관리자', value: UserRoleEnum['데이터 관리자'] },
        { label: '일반 회원', value: UserRoleEnum['일반 회원'] }
      ];
    } else if (loginUserRole === UserRoleEnum['최고 관리자']) {
      // 최고 관리자인 경우: 데이터 관리자, 일반 회원
      return [
        { label: '데이터 관리자', value: UserRoleEnum['데이터 관리자'] },
        { label: '일반 회원', value: UserRoleEnum['일반 회원'] }
      ];
    }
    // 기본값 (다른 권한인 경우)
    return [];
  };

  const handleSubmit = async () => {
    // 에러 초기화
    const newErrors = {
      name: '',
      email: '',
      password: '',
      passwordConfirm: '',
      contact: '',
      userIps: formData.userIps.map(() => ({ nickname: '', ip: '' }))
    };

    let hasError = false;

    // 유효성 검사
    if (!formData.name.trim()) {
      newErrors.name = '이름을 입력해주세요.';
      hasError = true;
    }

    const emailRegex = /^[^@]+@(?=.*[a-zA-Z])[^@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = '이메일을 입력해 주세요.';
      hasError = true;
    } else if (formData.email.trim() && !emailRegex.test(formData.email)) {
      newErrors.email = '올바른 이메일 형식을 입력해주세요.';
      hasError = true;
    }

    if (!formData.password || formData.password.length < 4 || formData.password.length > 20) {
      newErrors.password = '4~20자로 입력해 주세요.';
      hasError = true;
    }

    if (!passwordConfirm.trim()) {
      newErrors.passwordConfirm = '비밀번호를 입력해주세요.';
      hasError = true;
    } else if (formData.password !== passwordConfirm) {
      newErrors.passwordConfirm = '비밀번호가 일치하지 않습니다.';
      hasError = true;
    }

    if (!formData.contact.trim()) {
      newErrors.contact = '9~15자로 입력해주세요.';
      hasError = true;
    } else if (!/^\d+$/.test(formData.contact)) {
      newErrors.contact = '숫자만 입력해주세요.';
      hasError = true;
    } else if ((formData.contact.trim() && formData.contact.length < 9) || formData.contact.length > 15) {
      newErrors.contact = '9자 이상~15자 이하로 입력해주세요.';
      hasError = true;
    }

    // IP 필드 검증
    formData.userIps.forEach((ip, index) => {
      if (!ip.nickname.trim()) {
        newErrors.userIps[index].nickname = 'IP명을 입력해주세요.';
        hasError = true;
      }
      if (!ip.ip.trim()) {
        newErrors.userIps[index].ip = 'IP를 입력해주세요.';
        hasError = true;
      }
    });

    // 에러 상태 업데이트 (에러가 없으면 빈 문자열로 초기화됨)
    setErrors(newErrors);

    // 에러가 있으면 첫 번째 에러 필드로 스크롤 후 중단
    if (hasError) {
      // 스크롤 함수 (컨테이너 상단에 정확히 위치)
      const scrollToElementWithOffset = (element: HTMLElement | null) => {
        if (element && scrollContainerRef.current) {
          let targetElement = element;

          // HTMLInputElement인 경우, 조상 중 TextInput의 Box를 찾기
          if (element instanceof HTMLInputElement) {
            // input -> TextField wrapper -> Box
            const boxElement = element.parentElement?.parentElement?.parentElement;
            if (boxElement) {
              targetElement = boxElement;
            }
          }
          // div 등 다른 요소인 경우 해당 요소를 직접 사용

          const containerRect = scrollContainerRef.current.getBoundingClientRect();
          const inputGap = 24; // 인풋과 인풋 사이의 간격
          const targetRect = targetElement.getBoundingClientRect();
          const relativeTop = targetRect.top - containerRect.top;
          const scrollTop = scrollContainerRef.current.scrollTop;
          const targetScrollTop = scrollTop + relativeTop - inputGap;

          scrollContainerRef.current.scrollTo({
            top: targetScrollTop,
            behavior: 'smooth'
          });
        }
      };

      // 첫 번째 에러 필드 찾기 (순서대로)
      if (newErrors.name && nameRef.current) {
        scrollToElementWithOffset(nameRef.current);
      } else if (newErrors.email && emailRef.current) {
        scrollToElementWithOffset(emailRef.current);
      } else if (newErrors.password && passwordRef.current) {
        scrollToElementWithOffset(passwordRef.current);
      } else if (newErrors.passwordConfirm && passwordConfirmRef.current) {
        scrollToElementWithOffset(passwordConfirmRef.current);
      } else if (newErrors.contact && contactRef.current) {
        scrollToElementWithOffset(contactRef.current);
      } else {
        // IP 필드에서 첫 번째 에러 찾기
        for (let i = 0; i < newErrors.userIps.length; i++) {
          if (newErrors.userIps[i].nickname && ipRefs.current[i * 2]) {
            scrollToElementWithOffset(ipRefs.current[i * 2]);
            break;
          } else if (newErrors.userIps[i].ip && ipRefs.current[i * 2 + 1]) {
            scrollToElementWithOffset(ipRefs.current[i * 2 + 1]);
            break;
          }
        }
      }
      return;
    }

    // body 생성: 모든 값 trim 처리 및 contact에 countryCode 결합
    const body: IPostUserBody = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      password: formData.password.trim(),
      contact: `${formData.countryCode} ${formData.contact.trim()}`,
      userIps: formData.userIps.map((ip) => ({
        nickname: ip.nickname.trim(),
        ip: ip.ip.trim()
      })),
      role: formData.role
    };

    try {
      await postUserMutateAsync({
        body: body
      }).then(() => {
        setModalType(AdminModalType.USER_UPSERT_SUCCESS);
        setIsModalVisible(true);
      });
    } catch (error) {
      console.error('Failed to create a user. Error: ', error);
      setModalType(AdminModalType.USER_CREATE_FAIL);
      setIsModalVisible(true);
    }
  };

  const handleCancel = () => {
    setModalType(AdminModalType.USER_CREATE_CANCEL);
    setIsModalVisible(true);
  };

  const handleModalConfirmClick = () => {
    if (modalType === AdminModalType.USER_CREATE_CANCEL || modalType === AdminModalType.USER_UPSERT_SUCCESS) {
      navigate(PathnameEnum.ADMIN_USER_LIST_PAGE);
    } else if (modalType === AdminModalType.USER_CREATE_FAIL) {
      setIsModalVisible(false);
    }
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
  };

  return {
    formData,
    passwordConfirm,

    handleInputChange,
    handlePasswordConfirmChange,
    handleContactChange,
    handleIpChange,
    handleAddIp,
    handleRemoveIp,
    isAddIpButtonEnabled,
    getRoleOptions,

    errors,
    scrollContainerRef,
    nameRef,
    emailRef,
    passwordRef,
    passwordConfirmRef,
    contactRef,
    ipRefs,

    handleSubmit,

    handleCancel,
    isPostUserPending,
    modalType,
    isModalVisible,
    handleModalConfirmClick,
    handleModalClose
  };
};
